import React from "react";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";

const OverrideCreateDialog = ({
  open,
  onOpenChange,
  propEmployeeId,
  formData,
  setFormData,
  employees,
  schedules,
  handleCreateRequest,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("planning.overridesPanel.createTitle")}</DialogTitle>
          <DialogDescription>{t("planning.overridesPanel.createDescription")}</DialogDescription>
        </DialogHeader>

      <div className="space-y-4 py-4">
        {!propEmployeeId && (
          <div className="space-y-2">
            <Label>{t("planning.overridesPanel.employee")}</Label>
            <Select
              value={formData.employee_id?.toString()}
              onValueChange={(value) => setFormData({ ...formData, employee_id: parseInt(value) })}
            >
              <SelectTrigger><SelectValue placeholder={t("planning.overridesPanel.selectEmployee")} /></SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.last_name} {emp.first_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label>{t("planning.overridesPanel.date")}</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("planning.overridesPanel.requestedSchedule")}</Label>
          <Select
            value={formData.override_schedule_id?.toString()}
            onValueChange={(value) => setFormData({ ...formData, override_schedule_id: parseInt(value) })}
          >
            <SelectTrigger><SelectValue placeholder={t("planning.overridesPanel.selectSchedule")} /></SelectTrigger>
            <SelectContent>
              {schedules.map((s) => (
                <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t("planning.overridesPanel.priority")}</Label>
          <Select
            value={formData.priority.toString()}
            onValueChange={(value) => setFormData({ ...formData, priority: parseInt(value) })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">{t("planning.overridesPanel.priorities.low")}</SelectItem>
              <SelectItem value="5">{t("planning.overridesPanel.priorities.medium")}</SelectItem>
              <SelectItem value="10">{t("planning.overridesPanel.priorities.high")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t("planning.overridesPanel.reason")}</Label>
          <Textarea
            placeholder={t("planning.overridesPanel.reasonPlaceholder")}
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            rows={4}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="exceptional"
            checked={formData.is_exceptional}
            onCheckedChange={(checked) => setFormData({ ...formData, is_exceptional: checked })}
          />
          <Label htmlFor="exceptional" className="text-sm font-normal">
            {t("planning.overridesPanel.exceptional")}
          </Label>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("planning.scheduleManager.actions.cancel")}</Button>
        <Button onClick={handleCreateRequest}>
          <Send className="h-4 w-4 mr-2" /> {t("planning.overridesPanel.send")}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default OverrideCreateDialog;
